<template>
  <main>
    <div class="container">
      <div ref="stickySearch" class="row header--sticky sticky-container">
        <div class="col-full">
          <h1 class="heading-xl" data-test="faq-page-heading">
            {{ $t('faq.heading') }}
          </h1>
          <al-searchable-dropdown-input
            name="selection--change-options"
            :label="$t('faq.search.label')"
            :items="questionOptions"
            :min-match-char-length="3"
            :custom-validation="validation"
            match-highlight
            custom-event-handler
            has-clear-input-button
            @click-option="selectOption">
            <template #optionDescription="slotProps">
              <p v-if="!slotProps.numberOfOptions" class="heading-sm margin-bottom-none">
                {{ $t('faq.search.noResultsFound') }}
              </p>
              <p v-else-if="slotProps.numberOfOptions === 1" class="heading-sm margin-bottom-none">
                {{ $t('faq.search.oneResultFound') }}
              </p>
              <p v-else class="heading-sm margin-bottom-none">
                {{ $t('faq.search.resultsFound', { '9': slotProps.numberOfOptions }) }}
              </p>
            </template>
          </al-searchable-dropdown-input>
        </div>
      </div>

      <div v-for="category in faqs" :key="category.key + currentCountry" class="card">
        <h2 class="margin-bottom-none margin-left-md margin-top-md heading-md">
          {{ category.title }}
        </h2>
        <al-accordion :name="category.key" class="margin-bottom-none" embedded>
          <al-accordion-item
            v-for="qa in category.faq"
            :id="`faq-${qa.category}-${qa.key}`"
            :ref="`faq-${qa.category}-${qa.key}`"
            :key="qa.key + currentCountry"
            :name="`faq-${qa.category}-${qa.key}`"
            @open="track(qa, $EVENTS.FAQ_OPENED)"
            @close="track(qa, $EVENTS.FAQ_CLOSED)">
            <p slot="title" class="heading-sm">{{ qa.question }}</p>
            <p slot="body" class="heading-sm space-pre-wrap">
              <i18n :path="qa.answerKey">
                <span place="profileHome">
                  <a :href="$t('faq.profileHomeLink')" target="_blank">{{
                    $t('faq.profileHomeText')
                  }}</a>
                </span>
                <span place="privacyPolicy">
                  <a :href="privacyUrl" target="_blank">{{ $t('faq.privacyText') }}</a>
                </span>
                <span place="customerSupportPhone">
                  <a :href="customerServiceContact.href" class="no-wrap">{{
                    customerServiceContact.val
                  }}</a>
                </span>
              </i18n>
            </p>
          </al-accordion-item>
        </al-accordion>
      </div>
      <div v-if="!isLoggedIn" class="container">
        <div class="row-vertical-align-bottom" data-test="sign-in-button">
          <div class="col-full">
            <al-button
              v-analytics:click="$EVENTS.SIGN_IN"
              :label="$t('button.signIn')"
              name="sign-in-button"
              uppercase
              @click="$router.push({ name: ROUTES.ENTRY })" />
          </div>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import {
  AlAccordion,
  AlAccordionItem,
  AlButton,
  AlSearchableDropdownInput
} from '@mc-alberta/alcapone'
import { mapGetters, mapState } from 'vuex'
import faqJsonEnUs from '../content/en-US.json'
import { ROUTES } from '@/constants.js'

export default {
  name: ROUTES.FAQ,
  components: { AlAccordion, AlAccordionItem, AlButton, AlSearchableDropdownInput },
  data() {
    return {
      ROUTES,
      questionOptions: [],
      faqs: [],
      selectedItem: {},
      validation: {}
    }
  },
  computed: {
    ...mapGetters(['privacyUrl', 'isLoggedIn', 'locale']),
    ...mapState({
      currentCountry: (state) => state.user.consumer.countryCode
    }),
    customerServiceContact() {
      const countryConfig = this.$i18nData.getCountryConfig(this.currentCountry)
      const phoneNumber = countryConfig?.customerSupportPhoneNumber || ''

      return {
        href: `tel:${phoneNumber}`,
        val: phoneNumber
      }
    }
  },
  watch: {
    locale: {
      handler() {
        this.$nextTick(() => {
          this.faqs = this.buildFaq(faqJsonEnUs.faq)
        })
      }
    }
  },
  mounted() {
    this.faqs = this.buildFaq(faqJsonEnUs.faq)
  },
  methods: {
    track(questionDetails, event) {
      const { category: categoryKey, key: questionKey, question } = questionDetails || {}
      this.$analytics.track({
        event,
        meta: {
          faqSequence: `C${categoryKey}-Q${questionKey}`,
          faq: question
        }
      })
    },
    /**
     * Builds data structure based on the current locale's translated content.
     *
     * `faq.category1.a11` = "the answer for category 1 question number 1"
     * `faq.category1.q11` = "the question for category 1 question number 1"
     *
     *  We use en-US as a base set of translations that will contain the full set of question / answer keys.
     *  Other language may not contain all keys.
     *  For instance, fr-CA is an augmentation to fr-FR, so may not be complete.
     *
     */
    buildFaq(faqJson) {
      const orderedFAQ = {}
      this.questionOptions = []

      for (const categoryKey in faqJson) {
        if (categoryKey.includes('category')) {
          orderedFAQ[categoryKey] = this.createCategory(categoryKey, faqJson)
        }
      }
      const arrayFaq = []
      for (const categoryKey in orderedFAQ) {
        arrayFaq.push({ ...orderedFAQ[categoryKey], key: categoryKey })
      }

      arrayFaq.sort((c1, c2) => {
        return parseInt(c1.key.replace('category', '')) - parseInt(c2.key.replace('category', ''))
      })
      return arrayFaq
    },
    createCategory(categoryKey, faqJson) {
      const categoryIndex = parseInt(categoryKey.replace('category', ''))
      const category = {}
      category.title = this.$t(`faq.${categoryKey}.title`)
      category.faq = []

      const categoryJSON = faqJson[categoryKey]
      const partials = { title: category?.title }

      for (const questionOrAnswerKey in categoryJSON) {
        const key = questionOrAnswerKey.substring(1)

        if (!Object.hasOwn(partials, key)) {
          partials[key] = {}
        }

        if (questionOrAnswerKey[0] === 'q') {
          partials[key].question = this.$t(`faq.${categoryKey}.${questionOrAnswerKey}`)
        }

        if (questionOrAnswerKey[0] === 'a') {
          partials[key].answer = category[questionOrAnswerKey]
          partials[key].answerKey = `faq.${categoryKey}.${questionOrAnswerKey}`
          partials[key].category = categoryIndex
          partials[key].key = parseInt(key.replace(categoryIndex, ''))
        }

        if (Object.hasOwn(partials[key], 'question') && Object.hasOwn(partials[key], 'answer')) {
          category.faq.push({
            ...partials[key]
          })
          this.questionOptions.push({
            label: partials[key].question,
            value: `faq-${partials[key].category}-${partials[key].key}`
          })
        }
      }

      category.faq.sort((q1, q2) => {
        return q1.key - q2.key
      })
      return category
    },
    async selectOption(questionAnswer) {
      const element = this.$refs[questionAnswer.value][0]
      element.open()
      await this.$nextTick()

      // 50px is the height of the page header
      const stickySearchHeight = this.$refs.stickySearch.getBoundingClientRect().height + 50
      const scrollVal = element.$el.offsetTop
      window.scrollTo(0, scrollVal - stickySearchHeight)
      document.getElementById(questionAnswer.value).focus()
    }
  }
}
</script>

<style scoped>
.space-pre-wrap {
  white-space: pre-wrap;
}

.sticky-container {
  /* TODO: header--sticky should already apply an appropriate z-index but it is rendering above the menu which has a higher z-index. adding an additional lower z-index for now */
  z-index: var(--src-z-index-midground-high);
  top: calc(50 / 16 * 1rem);
  background-color: var(--src-color-background);
}
</style>
